/**
 * «С вашего прошлого визита» `/digest` (§8.1).
 *
 * Главный экран собственника: не журнал, а ответ на один вопрос — что
 * изменилось, пока меня не было, и на что стоит посмотреть прямо сейчас.
 *
 * Три жёстких правила спеки, которые здесь важнее красоты:
 *  1. метка «прошлый визит» сдвигается ТОЛЬКО по кнопке «Открыть таблицу» —
 *     случайный заход на сводку дифф не обнуляет;
 *  2. системный пакет импорта и пакеты-откаты из диффа исключены — иначе в день
 *     запуска сводка показала бы «10 412 правок, автор: Импорт из Google-таблицы»
 *     и доверие к режиму умерло бы в первый же день;
 *  3. любая строка «что стоит посмотреть» ведёт в таблицу с готовым фильтром и
 *     включённой линзой «изменилось с прошлого визита».
 *
 * Метка пока хранится локально: `users.last_digest_at` сервер не отдаёт.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { Brand, Change, ChannelCode } from '@shared/types.ts'
import {
  Avatar, Button, EmptyState, IconChevronDown, IconChevronRight, Skeleton,
} from '../../components/index.js'
import { useApp } from '../../store/app-store.js'
import { ChangeDiff } from '../history/ChangeDiff.js'
import { MAX_DETAILS, loadDigest, type DigestPayload } from '../history/digest-data.js'
import {
  channelFallbackNames, digestTexts, fieldGroupNames, fieldGroupOf, formatDateComma,
  formatShortDateTime, historyTexts, namesPreview, type FieldGroupCode,
} from '../history/texts.history.js'
import { digestSince, markVisitOpened } from '../history/visit-mark.js'
import { countOf, forms } from '../../texts.js'
import './DigestScreen.css'

type LoadState = 'loading' | 'ready' | 'error'

/** Пустое значение: сервер присылает и null, и пустую строку. */
const isBlank = (value: unknown): boolean => value === null || value === undefined || value === ''

function channelValues(brand: Brand, channel: ChannelCode) {
  return brand.channels[channel]
}

interface Insight {
  id: string
  text: string
  href: string
}

/**
 * Ссылка в таблицу с готовым фильтром и включённой линзой «изменилось с
 * прошлого визита» (§8.1). Имена параметров — контракт §10.5 главного экрана:
 * `tier`, `vis`, `nourl`, `cmt`, `lens`. Сборку делает URLSearchParams:
 * «A+» без кодирования превратилось бы в «A » (плюс читается как пробел).
 */
function brandsHref(channel: ChannelCode, extra: Record<string, string>): string {
  const params = new URLSearchParams()
  params.set('ch', channel)
  for (const [key, value] of Object.entries(extra)) params.set(key, value)
  params.set('lens', '1')
  return `/brands?${params.toString()}`
}

export function DigestScreen() {
  const navigate = useNavigate()
  const bootstrap = useApp((s) => s.bootstrap)
  const brands = useApp((s) => s.brands)
  const brandsById = useApp((s) => s.brandsById)

  const userId = bootstrap?.user.id ?? 0
  const users = bootstrap?.users ?? []
  const channel: ChannelCode = bootstrap?.settings.defaultChannel ?? 'retail'
  const channelName = bootstrap?.channels.find((c) => c.code === channel)?.name
    ?? channelFallbackNames[channel]

  const mark = useMemo(() => digestSince(userId), [userId])

  const [payload, setPayload] = useState<DigestPayload | null>(null)
  const [state, setState] = useState<LoadState>('loading')
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  useEffect(() => {
    let alive = true
    setState('loading')
    setError(null)
    loadDigest(mark.since)
      .then((res) => {
        if (!alive) return
        setPayload(res)
        setState('ready')
      })
      .catch((err: unknown) => {
        if (!alive) return
        setError(err instanceof Error ? err.message : digestTexts.errorTitle)
        setState('error')
      })
    return () => { alive = false }
  }, [mark.since, reloadToken])

  const openTable = useCallback(() => {
    // Единственное место, где метка визита сдвигается (§8.1).
    markVisitOpened(userId)
    navigate(`/brands?ch=${channel}`)
  }, [userId, channel, navigate])

  const changes: Change[] = useMemo(() => {
    if (!payload) return []
    const list: Change[] = []
    for (const pack of payload.packs) {
      const detail = payload.details.get(pack.id)
      if (detail) list.push(...detail.changes)
    }
    return list
  }, [payload])

  const totals = useMemo(() => {
    const packs = payload?.packs ?? []
    const changesCount = packs.reduce((sum, pack) => sum + pack.changesCount, 0)
    const authors = new Set(packs.map((pack) => pack.authorId))
    const newBrandIds = new Set(
      changes
        .filter((change) => change.action === 'create' && change.entityType === 'brand')
        .map((change) => change.brandId)
        .filter((id): id is number => id != null),
    )
    return { changesCount, packs: packs.length, authors: authors.size, newBrandIds }
  }, [payload, changes])

  /** «52 без URL (было 71)»: сколько было — считаем по правкам URL (§8.1). */
  const urlStat = useMemo(() => {
    let now = 0
    for (const brand of brands) {
      const values = channelValues(brand, channel)
      if (values?.isPresent && isBlank(values.url)) now += 1
    }
    let filled = 0
    let cleared = 0
    for (const change of changes) {
      if (change.fieldCode !== 'url' || change.key.channelCode !== channel) continue
      if (isBlank(change.oldValue) && !isBlank(change.newValue)) filled += 1
      if (!isBlank(change.oldValue) && isBlank(change.newValue)) cleared += 1
    }
    return { now, before: now + filled - cleared }
  }, [brands, channel, changes])

  const groups = useMemo(() => {
    const counts = new Map<FieldGroupCode, number>()
    for (const change of changes) {
      const group = fieldGroupOf(change)
      counts.set(group, (counts.get(group) ?? 0) + 1)
    }
    const rows = [...counts.entries()]
      .map(([code, count]) => ({ code, count, name: fieldGroupNames[code] }))
      .sort((a, b) => b.count - a.count)
    const max = rows.reduce((top, row) => Math.max(top, row.count), 0)
    return { rows, max }
  }, [changes])

  const authorRows = useMemo(() => {
    const counts = new Map<number, number>()
    for (const pack of payload?.packs ?? []) {
      counts.set(pack.authorId, (counts.get(pack.authorId) ?? 0) + pack.changesCount)
    }
    /* Короткие имена повторяются (в системе живут и рабочие, и проверочные
       учётки с одинаковой подписью). Два одинаковых чипа рядом читаются как
       ошибка, поэтому у совпавших показываем полное имя. */
    const shortNames = new Map<string, number>()
    for (const id of counts.keys()) {
      const short = users.find((user) => user.id === id)?.shortName
      if (short) shortNames.set(short, (shortNames.get(short) ?? 0) + 1)
    }
    return [...counts.entries()]
      .map(([id, count]) => {
        const user = users.find((item) => item.id === id)
        const ambiguous = user ? (shortNames.get(user.shortName) ?? 0) > 1 : false
        return {
          id,
          count,
          name: user ? (ambiguous ? user.fullName : user.shortName) : historyTexts.unknownAuthor,
        }
      })
      .sort((a, b) => b.count - a.count)
  }, [payload, users])

  const insights = useMemo<Insight[]>(() => {
    /* Бренда может не быть в текущем срезе — он в другом канале, в архиве или
       уже удалён. Имя тогда берём из самой правки: в сводке «#302» выглядит
       как сбой, а не как бренд. */
    const fromChanges = new Map<number, string>()
    for (const change of changes) {
      if (change.brandId == null || fromChanges.has(change.brandId)) continue
      const name = change.entityLabel.split(' · ')[0]?.trim()
      if (name) fromChanges.set(change.brandId, name)
    }
    const nameOf = (id: number): string =>
      brandsById.get(id)?.name ?? fromChanges.get(id) ?? `#${id}`
    const idsWhere = (predicate: (change: Change) => boolean): number[] => {
      const ids = new Set<number>()
      for (const change of changes) {
        if (change.brandId != null && predicate(change)) ids.add(change.brandId)
      }
      return [...ids]
    }

    const list: Insight[] = []

    const tierUp = idsWhere((change) =>
      change.entityType === 'brand_channel'
      && change.fieldCode === 'tier'
      && change.newValue === 'A+'
      && change.key.channelCode === channel)
    if (tierUp.length > 0) {
      list.push({
        id: 'tier-up',
        text: digestTexts.insight.tierUp(tierUp.length, namesPreview(tierUp.map(nameOf))),
        href: brandsHref(channel, { tier: 'A+' }),
      })
    }

    const hidden = idsWhere((change) =>
      change.fieldCode === 'visibility'
      && change.newValue === 'no'
      && change.key.channelCode === channel)
    if (hidden.length > 0) {
      list.push({
        id: 'hidden',
        text: digestTexts.insight.hidden(hidden.length, namesPreview(hidden.map(nameOf))),
        href: brandsHref(channel, { vis: 'no' }),
      })
    }

    const topWithoutUrl = brands.filter((brand) => {
      const values = channelValues(brand, channel)
      if (!values?.isPresent) return false
      return (values.tier === 'A+' || values.tier === 'A') && isBlank(values.url)
    })
    if (topWithoutUrl.length > 0) {
      list.push({
        id: 'no-url',
        text: digestTexts.insight.topWithoutUrl(topWithoutUrl.length),
        href: brandsHref(channel, { tier: 'A+,A', nourl: '1' }),
      })
    }

    const commented = idsWhere((change) => change.entityType === 'comment' && change.action === 'comment')
    if (commented.length > 0) {
      list.push({
        id: 'comments',
        text: digestTexts.insight.comments(commented.length, namesPreview(commented.map(nameOf))),
        href: brandsHref(channel, { cmt: '1' }),
      })
    }

    const created = [...totals.newBrandIds]
    if (created.length > 0) {
      list.push({
        id: 'new-brands',
        text: digestTexts.insight.newBrands(created.length, namesPreview(created.map(nameOf))),
        href: brandsHref(channel, {}),
      })
    }

    return list
  }, [changes, brands, brandsById, channel, totals])

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const heading = mark.exact ? digestTexts.titleWith(formatDateComma(mark.since)) : digestTexts.title

  if (state === 'loading') {
    return (
      <div className="digest">
        <h1 className="t-h1">{digestTexts.title}</h1>
        <Skeleton rows={8} columns={[200, 120, 120]} label={digestTexts.loadingLabel} />
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="digest">
        <h1 className="t-h1">{digestTexts.title}</h1>
        <EmptyState
          title={digestTexts.errorTitle}
          text={error}
          actions={<Button variant="primary" onClick={() => setReloadToken((n) => n + 1)}>{digestTexts.retry}</Button>}
        />
      </div>
    )
  }

  const packs = payload?.packs ?? []

  if (packs.length === 0) {
    return (
      <div className="digest">
        <header className="digest__head">
          <h1 className="t-h1">{heading}</h1>
          <Button variant="primary" size="l" onClick={openTable}>{digestTexts.openTable}</Button>
        </header>
        {mark.exact ? null : <p className="digest__nomark t-meta">{digestTexts.noMark}</p>}
        <EmptyState
          title={digestTexts.emptyTitle}
          text={digestTexts.emptyText(formatDateComma(mark.since))}
          actions={<Link className="digest__cta" to="/history">{digestTexts.toHistory}</Link>}
        />
      </div>
    )
  }

  return (
    <div className="digest">
      <header className="digest__head">
        <h1 className="t-h1">{heading}</h1>
        <div className="digest__head-action">
          <Button variant="primary" size="l" onClick={openTable}>{digestTexts.openTable}</Button>
          <span className="digest__head-hint t-meta">{digestTexts.openTableHint}</span>
        </div>
      </header>

      {mark.exact ? null : <p className="digest__nomark t-meta">{digestTexts.noMark}</p>}

      <section className="digest__stats" aria-label={digestTexts.title}>
        <Stat value={totals.changesCount} caption={digestTexts.stats.changes(totals.changesCount)} />
        <Stat value={totals.packs} caption={digestTexts.stats.packs(totals.packs)} />
        <Stat value={totals.authors} caption={digestTexts.stats.authors(totals.authors)} />
        <Stat value={totals.newBrandIds.size} caption={digestTexts.stats.newBrands(totals.newBrandIds.size)} />
        <Stat
          value={urlStat.now}
          caption={digestTexts.stats.noUrl}
          title={digestTexts.stats.noUrlTitle(channelName)}
          note={urlStat.before !== urlStat.now ? digestTexts.stats.noUrlWas(urlStat.before) : undefined}
        />
      </section>

      <p className="digest__excluded t-meta">{digestTexts.excluded}</p>

      {groups.rows.length > 0 ? (
        <section className="digest__block" aria-label={digestTexts.byType}>
          <h2 className="digest__h2 t-h2">{digestTexts.byType}</h2>
          <ul className="digest__bars">
            {groups.rows.map((row) => (
              <li className="digest__bar-row" key={row.code}>
                <span className="digest__bar-name t-cell">{row.name}</span>
                <span
                  className="digest__bar"
                  style={{ '--digest-bar': `${Math.round((row.count / Math.max(groups.max, 1)) * 100)}%` } as CSSProperties}
                  aria-hidden="true"
                />
                <span className="digest__bar-count t-cell">{row.count}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {authorRows.length > 0 ? (
        <section className="digest__block" aria-label={digestTexts.authors}>
          <h2 className="digest__h2 t-h2">{digestTexts.authors}</h2>
          <ul className="digest__authors">
            {authorRows.map((row) => (
              <li className="digest__author" key={row.id}>
                <Avatar name={row.name} />
                <Link className="digest__author-link t-cell" to={`/history?author=${row.id}`}>
                  {row.name}
                </Link>
                <span className="digest__author-count t-cell">{row.count}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {insights.length > 0 ? (
        <section className="digest__block" aria-label={digestTexts.worth}>
          <h2 className="digest__h2 t-h2">{digestTexts.worth}</h2>
          <ul className="digest__insights">
            {insights.map((insight) => (
              <li className="digest__insight" key={insight.id}>
                <span className="digest__insight-dot" aria-hidden="true">•</span>
                <span className="digest__insight-text t-cell">{insight.text}</span>
                <Link className="digest__insight-link t-cell" to={insight.href}>{digestTexts.show}</Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="digest__block" aria-label={digestTexts.packs}>
        <h2 className="digest__h2 t-h2">
          {digestTexts.packs}
          <span className="digest__h2-hint t-meta"> · {digestTexts.packsHint}</span>
        </h2>
        <ul className="digest__packs">
          {packs.map((pack) => {
            const detail = payload?.details.get(pack.id) ?? null
            const open = expanded.has(pack.id)
            const author = users.find((user) => user.id === pack.authorId)?.shortName
              ?? historyTexts.unknownAuthor
            return (
              <li className="digest__pack" key={pack.id}>
                <div className="digest__pack-row">
                  <Link className="digest__pack-id t-cell" to={`/history/${pack.id}`}>#{pack.id}</Link>
                  <span className="digest__pack-meta t-cell">
                    {author} · {formatShortDateTime(pack.committedAt)} · {countOf(pack.changesCount, forms.change)}
                    {pack.note ? ` · «${pack.note}»` : ''}
                  </span>
                  {detail ? (
                    <button
                      type="button"
                      className="digest__pack-toggle t-cell"
                      aria-expanded={open}
                      aria-label={open ? digestTexts.collapseLabel(pack.id) : digestTexts.expandLabel(pack.id)}
                      onClick={() => toggle(pack.id)}
                    >
                      {open ? digestTexts.collapse : digestTexts.expand}
                      {open ? <IconChevronDown /> : <IconChevronRight />}
                    </button>
                  ) : (
                    <Link className="digest__pack-toggle t-cell" to={`/history/${pack.id}`}>
                      {historyTexts.revert.openPack}
                    </Link>
                  )}
                </div>
                {open && detail ? (
                  <div className="digest__pack-diff">
                    <ChangeDiff changes={detail.changes} channel={channel} />
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
        {payload?.truncated ? (
          <p className="digest__truncated t-meta">{digestTexts.limited(MAX_DETAILS)}</p>
        ) : null}
      </section>
    </div>
  )
}

function Stat({ value, caption, note, title }: { value: number; caption: string; note?: string; title?: string }) {
  return (
    <div className="digest__stat" title={title}>
      <span className="digest__stat-value t-stat">{value}</span>
      <span className="digest__stat-caption t-meta">{caption}</span>
      {note ? <span className="digest__stat-note t-meta">{note}</span> : null}
    </div>
  )
}
